/**
 * <hauser-mfa-prompt> — Custom Element ESM (ha/359 Phase I).
 *
 * Affiche un modal de challenge TOTP. Reçoit le `session_token` du step
 * `mfa-challenge` (cf `docs/framework/hauser-form-protocol.md`), l'user
 * saisit le code à 6 chiffres, le widget POST vers `/ha/form` avec
 * `formId=ha.mfa.verify` + `mfa_token` + `mfa_code`.
 *
 * Si valide : le backend renvoie `{todo:"steps", steps:[…]}` qui redirige
 * vers `/compte`. Si invalide : message d'erreur inline + retry possible.
 *
 * Attributes :
 *   session-token  — token signé reçu du step mfa-challenge (requis)
 *   issuer         — nom de l'émetteur affiché (défaut "HorApp")
 *   account        — email/login affiché (optionnel)
 */

const TEMPLATE = `
<div class="hauser-mfa-backdrop" part="backdrop">
  <div class="hauser-mfa-card" role="dialog" aria-modal="true" aria-labelledby="hauser-mfa-title">
    <h2 id="hauser-mfa-title" class="hauser-mfa-title">Vérification en deux étapes</h2>
    <p class="hauser-mfa-sub">Saisissez le code à 6 chiffres généré par votre application d'authentification.</p>
    <form class="hauser-mfa-form" novalidate>
      <label class="hauser-mfa-label" for="hauser-mfa-code">Code TOTP</label>
      <input
        id="hauser-mfa-code"
        class="hauser-mfa-input"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        pattern="[0-9]{6}"
        maxlength="6"
        required
        aria-describedby="hauser-mfa-err"
      >
      <div id="hauser-mfa-err" class="hauser-mfa-error" role="alert" hidden></div>
      <button type="submit" class="hauser-mfa-submit">Valider</button>
    </form>
  </div>
</div>
`;

export class HauserMfaPrompt extends HTMLElement {
    private _submitting = false;

    static get observedAttributes(): string[] {
        return ["session-token", "csrf-token"];
    }

    connectedCallback(): void {
        if (this.children.length === 0) {
            this.innerHTML = TEMPLATE;
            this._wireSubmit();
            const account = this.getAttribute("account");
            if (account !== null && account !== "") {
                const sub = this.querySelector<HTMLElement>(".hauser-mfa-sub");
                if (sub !== null) sub.textContent = `Saisissez le code à 6 chiffres pour ${account}.`;
            }
            queueMicrotask(() => this._focusInput());
        }
    }

    private _wireSubmit(): void {
        const form = this.querySelector<HTMLFormElement>("form.hauser-mfa-form");
        if (form === null) return;
        form.addEventListener("submit", (e) => {
            e.preventDefault();
            void this._handleSubmit();
        });
    }

    private _focusInput(): void {
        const input = this.querySelector<HTMLInputElement>("input.hauser-mfa-input");
        if (input !== null) input.focus();
    }

    private async _handleSubmit(): Promise<void> {
        if (this._submitting) return;
        const input = this.querySelector<HTMLInputElement>("input.hauser-mfa-input");
        const errBox = this.querySelector<HTMLElement>("#hauser-mfa-err");
        if (input === null || errBox === null) return;
        const code = (input.value ?? "").trim();
        if (!/^\d{6}$/.test(code)) {
            this._showError(errBox, "Le code doit contenir exactement 6 chiffres.");
            return;
        }
        const token = this.getAttribute("session-token") ?? "";
        if (token === "") {
            this._showError(errBox, "Session MFA absente. Reconnectez-vous.");
            return;
        }

        this._submitting = true;
        try {
            const result = await this._postVerify(token, code);
            if (result.ok) {
                this.dispatchEvent(new CustomEvent("hauser-mfa:verified", {
                    bubbles:  true,
                    detail:   { response: result.payload },
                }));
                this.remove();
                return;
            }
            this._showError(errBox, result.message ?? "Code invalide. Réessayez.");
            input.value = "";
            input.focus();
        } catch (err) {
            this._showError(errBox, err instanceof Error ? err.message : "Erreur réseau.");
        } finally {
            this._submitting = false;
        }
    }

    private async _postVerify(token: string, code: string): Promise<{ ok: boolean; message?: string; payload: unknown }> {
        const body = new URLSearchParams({
            formId:    "ha.mfa.verify",
            mfa_token: token,
            mfa_code:  code,
        });
        // ha/359 — Si le step `mfa-challenge` a fourni un csrf-token frais
        // (rotaté côté serveur lors de la création de la session pending-MFA),
        // l'utiliser. Sinon retomber sur le meta du document.
        const explicitCsrf = this.getAttribute("csrf-token");
        const meta = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
        const csrf = (explicitCsrf !== null && explicitCsrf !== "") ? explicitCsrf : (meta?.content ?? "");
        const resp = await fetch("/ha/form", {
            method:  "POST",
            headers: {
                "X-CSRF-Token":     csrf,
                "X-Requested-With": "XMLHttpRequest",
                "Content-Type":     "application/x-www-form-urlencoded",
                "Accept":           "application/json",
            },
            body,
            credentials: "same-origin",
        });
        const payload = await resp.json() as { todo?: string; t?: string; message?: string };
        const ok = resp.ok && (payload.todo === "steps" || payload.t === "bulk" && Array.isArray((payload as { steps?: unknown[] }).steps));
        // Détection erreur : présence d'un msg de type "Code MFA invalide" en steps
        if (payload.t === "bulk" && Array.isArray((payload as { steps?: unknown[] }).steps)) {
            const steps = (payload as { steps: Array<{ t: string; m?: string }> }).steps;
            const errStep = steps.find(s => s.t === "msg" && typeof s.m === "string" && /invalid/i.test(s.m));
            if (errStep !== undefined) {
                return { ok: false, message: errStep.m, payload };
            }
        }
        return { ok, payload };
    }

    private _showError(box: HTMLElement, msg: string): void {
        box.textContent = msg;
        box.hidden = false;
    }
}

if (typeof customElements !== "undefined" && !customElements.get("hauser-mfa-prompt")) {
    customElements.define("hauser-mfa-prompt", HauserMfaPrompt);
}
